import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, Zap, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { SUPPORT_MAILTO } from '@/lib/contact';
import { safeRedirectPath } from '@/lib/safe-redirect';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';

export default function LoginPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const postLoginDestinationRef = React.useRef('/');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[LoginPage] Submit clicked');
    setIsLoading(true);

    const result = await login(email, password);

    if (result.success) {
      console.log('[LoginPage] Login successful, navigating to home');
      toast({
        title: t('auth.loginSuccessTitle'),
        description: t('auth.loginSuccessBody'),
      });
    } else {
      console.warn('[LoginPage] Login failed', { error: result.error });
      toast({
        title: t('auth.loginFailedTitle'),
        description: result.error || t('auth.loginFailedBody'),
        variant: 'destructive',
      });
    }

    setIsLoading(false);
  };

  // Read redirect_to from query params to preserve navigation intent. Only a
  // path on this site; PublicRoute (App.tsx) applies the same rule.
  const redirectTo = safeRedirectPath(searchParams.get('redirect_to'));
  useEffect(() => {
    if (redirectTo) {
      postLoginDestinationRef.current = redirectTo;
    }
  }, [redirectTo]);

  useEffect(() => {
    if (user) {
      navigate(postLoginDestinationRef.current);
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden px-4">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-hero">
        <motion.div
          className="absolute top-1/3 right-1/4 w-80 h-80 rounded-full bg-primary/20 blur-[100px]"
          animate={{
            x: [0, 30, 0],
            y: [0, -20, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/3 left-1/4 w-80 h-80 rounded-full bg-accent/20 blur-[100px]"
          animate={{
            x: [0, -30, 0],
            y: [0, 20, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-glow">
            <Zap className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-2xl font-bold font-display">
            <span className="gradient-text">Face</span>Shot
          </span>
        </Link>

        {/* Card */}
        <div className="glass-card-elevated p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-2">{t('auth.welcomeBack')}</h1>
            <p className="text-muted-foreground">{t('auth.signInToAccount')}</p>
          </div>

          {/* Google above the form (2026-09-25). Its session reaches `user`
              exactly like a password login's, so the redirect_to effect
              above (and PublicRoute) take it from there. */}
          <GoogleSignInButton source="login_page" />
          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <span aria-hidden="true" className="h-px flex-1 bg-border" />
            {t('auth.orDivider')}
            <span aria-hidden="true" className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('auth.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <a
                href={`${SUPPORT_MAILTO}?subject=Password%20reset%20request`}
                className="text-sm text-primary hover:underline"
              >
                {t('auth.forgotPassword')}
              </a>
            </div>

            <Button
              type="submit"
              variant="hero"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? t('auth.signingIn') : t('auth.signIn')}
              {!isLoading && <ArrowRight className="w-4 h-4 ml-1" />}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground text-sm">
              {t('auth.dontHaveAccount')}{' '}
              <Link
                to={redirectTo ? `/signup?redirect_to=${encodeURIComponent(redirectTo)}` : '/signup'}
                className="text-primary font-semibold hover:underline"
              >
                {t('auth.signUpFree')}
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
